import {Rox} from 'rox-ssr';

import {API_URL} from 'constants/api';
import {ENDPOINTS} from 'constants/endpoints';
import {CLIENT_ID, SITE_ID} from 'constants/app';
import {serializeToFormData} from 'libs/utils/utils';
import {OAuthSocialType, OAuthTokenType} from 'reducers/oAuth/oAuthType';
import {instanceGusaBackend} from 'libs/api';
import {AxiosErrorCustom} from '@typescript/common/authorizationTypes';

/**
 * Add expires date for OAuth token data
 * @param {OAuthTokenType | string} data - OAuth token data
 * @returns {OAuthTokenType} OAuth token data with expires date
 */
export const addExpiresDate = (data: OAuthTokenType | string): OAuthTokenType => {
    // Fix for IE11
    const parsedData: OAuthTokenType = typeof data === 'string' ? JSON.parse(data) : data;

    const today = new Date();
    today.setSeconds(today.getSeconds() + parsedData.expires_in);

    return {
        ...parsedData,
        expiresDate: today.getTime(),
    };
};

/**
 * Extracts error message from token response
 * @param {AxiosErrorCustom<{message: string}>} error - error response
 * @return {string} error message
 */
export const parseMessageFromError = (error: AxiosErrorCustom<{message: string}>): string =>
    error.response ? error.response.data.message || '' : '';

export const customer = {
    /**
     * Creates request to get user token
     * @param {string} email user email
     * @param {string} password user password
     * @returns {Promise<[]>} promise with request to get user token
     */
    fetchToken: async (email: string, password: string): Promise<[null | string, number]> => {
        try {
            const {
                data: {code},
            } = await instanceGusaBackend({
                method: 'post',
                baseURL: API_URL,
                url: `${ENDPOINTS.CUSTOMER_TOKEN}`,
                data: serializeToFormData({
                    grant_type: 'password',
                    website_id: SITE_ID,
                    client_id: CLIENT_ID,
                    username: email,
                    password,
                }),
                transformResponse: [addExpiresDate],
                responseType: 'json',
            });

            return [null, code];
        } catch (error) {
            return [parseMessageFromError(error), 0];
        }
    },
    /**
     * Send request for refresh customer token
     * @param {string} refreshToken - Customer OAuth token
     * @return {Promise<[]>} Response from backend
     */
    refreshToken: async (refreshToken: string): Promise<[null | string]> => {
        try {
            await instanceGusaBackend({
                method: 'post',
                baseURL: API_URL,
                url: `${ENDPOINTS.CUSTOMER_TOKEN}`,
                data: serializeToFormData({
                    grant_type: 'refresh_token',
                    client_id: CLIENT_ID,
                    refresh_token: refreshToken,
                }),
                transformResponse: [addExpiresDate],
                responseType: 'json',
            });

            return [null];
        } catch (error) {
            return [parseMessageFromError(error)];
        }
    },
    /**
     * Send request for social auth
     * @param {OAuthSocialType} data - data for social auth
     * @return {Promise<[]>} Response from backend
     */
    fetchTokenSocial: async (data: OAuthSocialType): Promise<[null | string]> => {
        try {
            await instanceGusaBackend({
                method: 'post',
                baseURL: API_URL,
                url: `${ENDPOINTS.CUSTOMER_TOKEN}`,
                data: serializeToFormData({
                    grant_type: 'social',
                    client_id: CLIENT_ID,
                    access_token: data.accessToken,
                    provider: data.provider,
                }),
                transformResponse: [addExpiresDate],
                responseType: 'json',
            });

            return [null];
        } catch (error) {
            return [parseMessageFromError(error)];
        }
    },
    /**
     * Send request for customer logout
     * @return {Promise<[]>} Response from backend
     */
    logout: async (): Promise<[null | string]> => {
        try {
            await instanceGusaBackend.get(ENDPOINTS.CUSTOMER_LOGOUT);
            Rox.setCustomBooleanProperty('isHto', false);
            return [null];
        } catch (error) {
            return ['An error occurs on logout'];
        }
    },
};
